// Phase 11 — model-document generation picker + trigger.
//
// GenerateDocButton drops into the Documents-section header of CaseView and
// CollectiveCaseView (beside the add-link button). It self-gates on
// caseView.documents.generate and owns the modal's open state, so the
// minified views need no new hooks — they just render the button.
//
// The modal BRANCHES on the template kind. Prose templates show a subset of
// clients/opponents; inventory (enumeration) templates hide the parties picker
// and instead show a folder-to-enumerate picker. Both kinds pick a language
// (only languages with an uploaded variant), a procedural stage (resolves
// jurisdiction + register number), and a destination subfolder (enumerated
// live). The inventory target pre-fills from — and locks to — the template's
// fixed target (LEGALREF) when one is configured, or is freely picked at
// generate time (INV/INVcoll). On success the .docx downloads immediately and
// is saved to the chosen subfolder; loose files skipped by the inventory walk
// are surfaced as a PERSISTENT warning (a skipped exhibit has legal weight).

import React, { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { compareMemberNames, type NamedClient } from "../lib/memberOrder"; // 216 §2 [RULED 71] — the parties' one order
import type { TFn } from "../lib/taxonomyTypes"; // 204 §F — the translator's shape, narrowed at the seam as BentoBoard does
import { usePermissions } from "../hooks/usePermissions";
import { getDocTemplates, getDocGenSubfolders, generateDocument } from "../api";
import { ModalLayout, SaveButton, CancelButton } from "./SharedUI";
import { FileText, Download, AlertTriangle, Check } from "lucide-react";
import { CT, RAD, FS } from "../lib/tokens";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { resolveTaxonomyLabel } from "../lib/taxonomyHelpers";
import MenuSelect from "./MenuSelect"; // 154B §2
import StatusPill from "./StatusPill"; // 168 §1b — the roster's pill, inert arm
import { linkStatusCfg } from "../lib/linkStatus"; // 168 §1b — one definition

const idsFrom = (arr: any) =>
  (Array.isArray(arr) ? arr : [])
    .map((x) =>
      typeof x === "number"
        ? x
        : x && typeof x === "object"
          ? (x.id ?? x.clId)
          : null,
    )
    .filter((n) => typeof n === "number");
/** 168 §1b — a party row as this modal reads one: an id, and the name fields
 *  `dispName` picks over. */
type PartyOpt = { id: number; [k: string]: unknown };
const dispName = (p: any) =>
  p
    ? p.companyName ||
      p.name ||
      [p.firstName, p.lastName].filter(Boolean).join(" ") ||
      "#" + p.id
    : "";

export function GenerateDocButton({
  kind,
  entity,
  cl,
  op,
  stages,
  stageTypesList,
  toast,
  style,
  assigned,
  presetTplId,
  disabled,
  label,
}: any) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const scopeArg = kind === "cc" ? { ccId: entity.id } : { caseId: entity.id };
  const [open, setOpen] = useState(false);
  // Generate is assignment-based: any collaborator assigned to the case (the
  // `assigned` prop, computed by the view from RP/admin/junction membership)
  // may generate, regardless of role default. can() still covers admin/RP/grant
  // and is the server-enforced gate; this OR mirrors the server's assignment
  // overlay so the button shows for the team.
  if (!(assigned || can("caseView.documents.generate", scopeArg))) return null;
  return (
    <>
      <button
        type="button"
        className="btn-unified btn-unified-primary"
        style={style}
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        <FileText size={11} />{" "}
        {label || t("docgen.generate", { fallback: "Generate" })}
      </button>
      {open && (
        <GenerateDocModal
          kind={kind}
          entity={entity}
          cl={cl || []}
          op={op || []}
          stages={stages || []}
          stageTypesList={stageTypesList || []}
          toast={toast}
          onClose={() => setOpen(false)}
          initialTplId={presetTplId}
        />
      )}
    </>
  );
}

function GenerateDocModal({
  kind,
  entity,
  cl,
  op,
  stages,
  // 05n — kept in the signature though `stageLabel` no longer reads it: the
  // prop is threaded from two views through CaseDocumentsSection and
  // GenerateDocButton, and removing it is a four-file change in a slice that
  // is already wide. Registered, not smuggled.
  stageTypesList,
  toast,
  onClose,
  initialTplId,
}: any) {
  const { t } = useTranslation() as { t: TFn };
  const caseStageEntries = useTaxonomy("case_stage").entries;

  const clientIds = idsFrom(kind === "cc" ? entity.clientLinks : entity.clIds);
  const oppIds = idsFrom(entity.opIds);
  // 168 §1b — the client rows are named (an id and whatever else a party
  // carries) so that the two new reads below — the untick and the pill — are
  // typed rather than `any.id`. `cl` itself is `any` at this boundary, like
  // every prop here; the narrowing stops at the one list this slice reads.
  /* 216 §2 [RULED 71 — amends 204 §F] — the chooser's two lists, in the one
     order of a matter's parties. 204 §F sorted them by the name the row shows
     (`dispName`, first name first) — which is not the order: ruling 71 files
     every list of a matter's people by last name, then first name, a company
     by its name, the title, accents and case ignored, as the collective case's
     client list does (208 §6) and as the generated document now lists them
     (server/src/lib/docGen.js, the loader). One comparator for all three,
     src/lib/partyOrder.mjs; a tie falls to the id. The rows still READ first
     name first (`dispName`): the order changes, never the name shown. Both
     lists start in the LINK order (the case's `clIds` / the collective case's
     `clientLinks`, and `opIds`, as the parties were attached), which reads as
     random — so both are sorted here. */
  const byPartyName = (a: PartyOpt, b: PartyOpt) => compareMemberNames(a as NamedClient, b as NamedClient) || a.id - b.id;
  const clientOpts = clientIds
    .map((id) => (cl as PartyOpt[]).find((c) => c.id === id))
    .filter((c): c is PartyOpt => !!c)
    .sort(byPartyName);
  /* 168 §1b — RULING 4 (Laurent, 2026-09-05): generated documents offer
     Prospect members UNTICKED, marked with their pill. A claimant list never
     gains an enquirer by accident, and one can still be included on purpose.

     ONE VOCABULARY, TWO SHAPES, and both are read here. The brief measured the
     collective one (`entity.clientLinks`, an array of `{clId, status, …}`);
     re-measuring found the SAME value on a regular case, in `clLinks` — the
     `{clientId: {status, …dates}}` map `lib/shapes.js:121` puts on every case
     read, which 141 §2.2 built when the case's parties pane grew the CCV
     roster's control ("same vocabulary, same colours"). Ruling 4 names Prospect
     MEMBERS, not a kind of matter, so the untick follows the value rather than
     the container. On a case the value is deliberate — `linkStatus.ts` records
     that a case link is NULL until somebody rules on it — which makes it more
     of an instruction, not less.

     Only the explicit value matters, as in §1a: no status is not a Prospect.

     `entity` is `any` at this component's boundary (the whole props object is,
     and widening it is a four-file change this slice is not); the two shapes
     are named HERE instead, so everything below this line is typed and the
     file's ratchets do not move. */
  const linked = entity as {
    clientLinks?: { clId?: number; id?: number; status?: string | null }[];
    clLinks?: Record<string, { status?: string | null } | undefined>;
  };
  const linkStatusOf = (id: number): string | null => {
    if (kind === "cc") {
      const rows = Array.isArray(linked.clientLinks) ? linked.clientLinks : [];
      const lk = rows.find((x) => Number(x.clId ?? x.id) === id);
      return lk?.status ?? null;
    }
    return linked.clLinks?.[String(id)]?.status ?? null;
  };
  const isProspectMember = (id: number) => linkStatusOf(id) === "Prospect";
  const oppOpts = oppIds
    .map((id) => (op as PartyOpt[]).find((o) => o.id === id))
    .filter((o): o is PartyOpt => !!o)
    .sort(byPartyName); // 204 §7.3, 216 §2 — the same order

  const [templates, setTemplates] = useState<any>(null);
  // When opened from the in-pane template picker (Phase 12 Stage 3), the
  // template is already chosen — initialise from initialTplId so the modal
  // skips the picker and shows the remaining choices straight away.
  const presetTplStr =
    initialTplId != null && initialTplId !== "" ? String(initialTplId) : "";
  const [tplId, setTplId] = useState(presetTplStr);
  const tplPreset = !!presetTplStr;
  const [lang, setLang] = useState("");
  const [stageId, setStageId] = useState("");
  const [dest, setDest] = useState("");
  const [target, setTarget] = useState("");
  const [subfolders, setSubfolders] = useState<any>(null);
  // 168 §1b (ruling 4) — everyone starts ticked EXCEPT a Prospect member. The
  // row is still offered, and one tick includes them.
  const [selClients, setSelClients] = useState(
    () =>
      new Set(
        clientOpts.filter((c) => !isProspectMember(c.id)).map((c) => c.id),
      ),
  );
  const [selOpps, setSelOpps] = useState(
    () => new Set(oppOpts.map((o) => o.id)),
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<any>(null);

  // Load templates (only those with at least one uploaded variant) +
  // destination subfolders (live).
  useEffect(() => {
    getDocTemplates()
      .then((rows) =>
        setTemplates(
          // Invoice (pro-forma) templates are generated from the pro-forma view
          // (they merge server-assembled invoice data), not the case generator.
          (Array.isArray(rows) ? rows : []).filter(
            (x) => x.languages && x.languages.length && x.kind !== "invoice",
          ),
        ),
      )
      .catch((e) => {
        toast?.("⚠ " + (e?.message || t("docTpl.load_failed", { fallback: "Failed to load templates" })));
        setTemplates([]);
      });
    (getDocGenSubfolders(kind, entity.id) as Promise<{ subfolders?: unknown[] } | null>)
      .then((r) =>
        setSubfolders(Array.isArray(r?.subfolders) ? r.subfolders : []),
      )
      .catch((e) => {
        setSubfolders([]);
        toast?.(
          "⚠ " +
            (e?.code === "docgen.no_case_folder"
              ? t("docgen.no_case_folder", {
                  fallback: "This case has no linked SharePoint folder",
                })
              : e?.message || t("docgen.subfolders_failed", { fallback: "Failed to list subfolders" })),
        );
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tpl = templates && templates.find((x: any) => x.id === Number(tplId));
  // Keep the language valid for the chosen template.
  useEffect(() => {
    if (tpl && !tpl.languages.includes(lang)) setLang(tpl.languages[0] || "");
  }, [tplId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEnum = tpl && tpl.kind === "enumeration";
  // Inventory target: a configured targetSubfolder (LEGALREF) is FIXED — the
  // server uses it and ignores any client override — so the picker pre-fills
  // and locks to it. INV/INVcoll have no fixed target and pick at generate time.
  const targetFixed = isEnum && !!tpl.targetSubfolder;
  const needsTarget = isEnum && !targetFixed;

  // Pre-fill (and lock) the inventory target for fixed-target templates; clear
  // it otherwise so INV/INVcoll start empty and force a deliberate pick.
  useEffect(() => {
    setTarget(
      tpl && tpl.kind === "enumeration" && tpl.targetSubfolder
        ? tpl.targetSubfolder
        : "",
    );
  }, [tplId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (set: any, setter: any, id: any) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  // 05n — A READER 05m STEP C MISSED, and the shape of the miss is worth
  // keeping. `stageTypesList` is `case_stage` through toDisplayList, which for
  // that kind returned a list of STRINGS. `.find(x => x.id === s.type)` looks
  // for `.id` on a string, so it never matched — not once, for anybody — and
  // every stage in this dialog fell through to `s.type` RAW. That printed
  // "Appeal" inside a French docgen dialog, and after this slice's conversion
  // it would have printed `appeal`.
  //
  // Fixed through the resolver rather than by reading the tuple's second
  // element, because those are not the same answer: the tuple only knows the
  // code, and a stage row may still hold a LABEL (brief 23 §3's residue, or a
  // client that has not reloaded). `resolveTaxonomyLabel` matches all five
  // facets and returns the stored string unchanged when nothing claims it —
  // which is the ruled behaviour for prod's one orphan, `"Memo, legal opinion"`:
  // it keeps saying what the record holds. `<option>` text cannot carry
  // `TaxonomyOrphanMark`, so this is the honest maximum here.
  const stageLabel = (s: any, i: any) => {
    const ty =
      resolveTaxonomyLabel(s.type, caseStageEntries) ||
      t("docgen.stage", { fallback: "Stage" });
    const reg = s.registerNumber ? ` · ${s.registerNumber}` : "";
    const dt = s.startDate ? ` (${String(s.startDate).slice(0, 10)})` : "";
    return `${i + 1}. ${ty}${reg}${dt}`;
  };

  const submit = async () => {
    if (!tpl) {
      toast?.(
        "⚠ " + t("docgen.pick_template", { fallback: "Pick a template" }),
      );
      return;
    }
    if (!lang) {
      toast?.(
        "⚠ " + t("docgen.pick_language", { fallback: "Pick a language" }),
      );
      return;
    }
    if (!dest) {
      toast?.(
        "⚠ " +
          t("docgen.pick_dest", { fallback: "Pick a destination subfolder" }),
      );
      return;
    }
    if (needsTarget && !target) {
      toast?.(
        "⚠ " +
          t("docgen.pick_target", { fallback: "Pick the folder to inventory" }),
      );
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        templateId: tpl.id,
        language: lang,
        stageId: stageId ? Number(stageId) : null,
        clientIds: [...selClients],
        opponentIds: [...selOpps],
        destSubfolder: dest,
        ...(isEnum && target ? { targetSubfolder: target } : {}),
      };
      const res = (await generateDocument(kind, entity.id, payload)) as {
        docBase64: string;
        mimeType?: string;
        fileName?: string;
        saved?: { ok?: boolean };
      };
      // Trigger the download.
      const bytes = Uint8Array.from(atob(res.docBase64), (c) =>
        c.charCodeAt(0),
      );
      const blob = new Blob([bytes], {
        type: res.mimeType || "application/octet-stream",
      });
      const { saveAs } = await import("file-saver" as any);
      saveAs(blob, res.fileName);
      setResult(res);
      if (res.saved && res.saved.ok)
        toast?.(
          t("docgen.saved", { fallback: "Document generated and saved" }),
        );
      else
        toast?.(
          "⚠ " +
            t("docgen.saved_failed", {
              fallback:
                "Generated and downloaded, but saving to SharePoint failed",
            }),
        );
    } catch (e) {
      toast?.(
        "⚠ " +
          ((e as any)?.message || t("docgen.failed", { fallback: "Generation failed" })),
      );
    } finally {
      setBusy(false);
    }
  };

  const lbl = {
    fontSize: FS.s10,
    fontWeight: 600,
    color: "var(--tp)",
    display: "block",
    marginBottom: 3,
    marginTop: 8,
  };

  const footer = (
    <div className="modal-footer">
      <CancelButton
        label={t("common.close", { fallback: "Close" })}
        onClick={onClose}
      />
      <SaveButton
        label={
          busy
            ? t("docgen.generating", { fallback: "Generating…" })
            : t("docgen.generate", { fallback: "Generate" })
        }
        onClick={submit}
      />
    </div>
  );

  return (
    <ModalLayout
      title={t("docgen.title", { fallback: "Generate document" })}
      onClose={onClose}
      footer={footer}
      maxWidth={520}
    >
      {/* Persistent loose-file warning — stays until the lawyer closes. */}
      {result &&
        Array.isArray(result.looseFiles) &&
        result.looseFiles.length > 0 && (
          <div
            style={{
              border: `1px solid var(--rd, ${CT.c_f87171})`,
              background: "rgba(248,113,113,.12)",
              borderRadius: RAD.r6,
              padding: 10,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 600,
                color: `var(--rd, ${CT.c_f87171})`,
                marginBottom: 4,
              }}
            >
              <AlertTriangle size={14} />{" "}
              {t("docgen.loose_warning", {
                fallback: "Files skipped — not in a subfolder",
              })}
            </div>
            <p style={{ fontSize: FS.s11, lineHeight: 1.5, margin: "0 0 6px" }}>
              {t("docgen.loose_warning_body", {
                fallback:
                  "These files sit directly in the target folder, not inside a numbered subfolder, so they were NOT included in the inventory. Move them into a subfolder and regenerate if they belong in the list:",
              })}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: FS.s11 }}>
              {result.looseFiles.map((f: any, i: any) => (
                <li
                  key={i}
                  style={{ fontFamily: "'JetBrains Mono',monospace" }}
                >
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      {result && (
        <div
          style={{
            border: "1px solid var(--br)",
            borderRadius: RAD.r6,
            padding: 10,
            marginBottom: 10,
            fontSize: FS.s11_5,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "var(--gn)",
              fontWeight: 600,
            }}
          >
            {result.saved && result.saved.ok ? (
              <Check size={14} />
            ) : (
              <AlertTriangle size={14} />
            )}
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                color: "var(--tp)",
              }}
            >
              {result.fileName}
            </span>
          </div>
          <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "4px 0 0" }}>
            {result.saved && result.saved.ok
              ? t("docgen.saved_to", { fallback: "Saved to" }) +
                " " +
                result.destPath
              : t("docgen.saved_failed_detail", {
                  fallback:
                    "Downloaded to your computer; SharePoint save failed",
                }) +
                (result.saved?.error ? " (" + result.saved.error + ")" : "")}
          </p>
        </div>
      )}

      {!tplPreset && (
        <>
          <label style={{ ...lbl, marginTop: 0 }}>
            {t("docgen.template", { fallback: "Template" })}
          </label>
          <MenuSelect
            className="field-input"
            value={tplId}
            onChange={(v) => setTplId(v)}
            disabled={busy}
            options={[
              ["", t("docgen.choose", { fallback: "— Choose —" })] as const,
              /* the option's five children were adjacent text nodes */
              ...(templates || []).map(
                (x: any) =>
                  [
                    String(x.id),
                    `${x.code} — ${x.name} (${
                      x.kind === "enumeration"
                        ? t("docTpl.kind_enum", { fallback: "Enumeration" })
                        : t("docTpl.kind_prose", { fallback: "Prose" })
                    })`,
                  ] as const,
              ),
            ]}
          />
          {templates && templates.length === 0 && (
            <p style={{ fontSize: FS.s10_5, color: "var(--tm)", marginTop: 4 }}>
              {t("docgen.no_templates", {
                fallback:
                  "No templates with an uploaded file. Add one in Settings → Document Templates.",
              })}
            </p>
          )}
        </>
      )}
      {tplPreset && tpl && (
        <p
          style={{
            fontSize: FS.s11,
            color: "var(--ts)",
            margin: "0 0 4px",
            fontWeight: 500,
          }}
        >
          <FileText
            size={11}
            style={{
              verticalAlign: "middle",
              marginRight: 4,
              color: "var(--ac)",
            }}
          />
          {tpl.code} — {tpl.name}
          <span style={{ fontSize: FS.s9_5, color: "var(--tm)", marginLeft: 6 }}>
            (
            {tpl.kind === "enumeration"
              ? t("docTpl.kind_enum", { fallback: "Enumeration" })
              : t("docTpl.kind_prose", { fallback: "Prose" })}
            )
          </span>
        </p>
      )}

      {tpl && (
        <>
          <label style={lbl}>
            {t("docgen.language", { fallback: "Language" })}
          </label>
          <MenuSelect
            className="field-input"
            value={lang}
            onChange={(v) => setLang(v)}
            disabled={busy}
            options={tpl.languages.map(
              (l: any) => [String(l), String(l).toUpperCase()] as const,
            )}
          />

          {/* Parties (prose only). Inventory templates hide the subset picker;
              the full party set is still sent so any header tags resolve. */}
          {!isEnum && (clientOpts.length > 0 || oppOpts.length > 0) && (
            <>
              {clientOpts.length > 0 && (
                <>
                  <label style={lbl}>
                    {t("docgen.clients", { fallback: "Clients to include" })}
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {clientOpts.map((c) => (
                      <label
                        key={c.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: FS.s11,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selClients.has(c.id)}
                          onChange={() =>
                            toggle(selClients, setSelClients, c.id)
                          }
                          disabled={busy}
                        />
                        {dispName(c)}
                        {/* 168 §1b — THE ROSTER'S OWN PILL, NOT A THIRD ONE.
                            `linkStatusCfg` is the definition 141 §2.2 moved to
                            `lib/linkStatus.ts` precisely so the CCV roster and
                            the case's parties pane could stop keeping two
                            colour tables in step; a hardcoded lavender chip
                            here would have been the third. StatusPill with no
                            `options`/`onPick` is its inert display variant
                            (136 §1) — the same span the roster draws, with no
                            menu, because this is a label and not a control.
                            Only Prospect is marked: ruling 4 marks the row it
                            unticks, and a pill on every member would be a
                            status column nobody asked this modal for. */}
                        {isProspectMember(c.id) && (
                          <StatusPill cfg={linkStatusCfg(t, "Prospect")} />
                        )}
                      </label>
                    ))}
                  </div>
                </>
              )}
              {oppOpts.length > 0 && (
                <>
                  <label style={lbl}>
                    {t("docgen.opponents", {
                      fallback: "Opponents to include",
                    })}
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {oppOpts.map((o) => (
                      <label
                        key={o.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: FS.s11,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selOpps.has(o.id)}
                          onChange={() => toggle(selOpps, setSelOpps, o.id)}
                          disabled={busy}
                        />
                        {dispName(o)}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          <label style={lbl}>
            {t("docgen.stage", {
              fallback: "Procedural stage (jurisdiction + register)",
            })}
          </label>
          <MenuSelect
            className="field-input"
            value={stageId}
            onChange={(v) => setStageId(v)}
            disabled={busy}
            options={[
              [
                "",
                t("docgen.no_stage", {
                  fallback: "— None (case-level jurisdiction) —",
                }),
              ] as const,
              ...(stages || []).map(
                (st: any, i: any) => [String(st.id), stageLabel(st, i)] as const,
              ),
            ]}
          />

          {isEnum && (
            <>
              <label style={lbl}>
                {t("docgen.target", { fallback: "Folder to inventory" })}
              </label>
              <MenuSelect
                className="field-input"
                value={target}
                onChange={(v) => setTarget(v)}
                disabled={busy || subfolders === null || targetFixed}
                options={[
                  ["", t("docgen.choose", { fallback: "— Choose —" })] as const,
                  ...(subfolders || []).map((f: any) => [String(f), String(f)] as const),
                  /* The fixed target may not (yet) exist among the live
                     subfolders; surface it so the locked picker still shows
                     the bound folder. */
                  ...(targetFixed &&
                  tpl.targetSubfolder &&
                  !(subfolders || []).includes(tpl.targetSubfolder)
                    ? [[tpl.targetSubfolder, tpl.targetSubfolder] as const]
                    : []),
                ]}
              />
              {targetFixed && (
                <p style={{ fontSize: FS.s10_5, color: "var(--tm)", marginTop: 4 }}>
                  {t("docgen.fixed_target", {
                    fallback: "Inventory target is fixed by this template.",
                  })}
                </p>
              )}
            </>
          )}

          <label style={lbl}>
            {t("docgen.dest", { fallback: "Save into subfolder" })}
          </label>
          <MenuSelect
            className="field-input"
            value={dest}
            onChange={(v) => setDest(v)}
            disabled={busy || subfolders === null}
            options={[
              ["", t("docgen.choose", { fallback: "— Choose —" })] as const,
              ...(subfolders || []).map((f: any) => [String(f), String(f)] as const),
            ]}
          />
          {subfolders !== null && subfolders.length === 0 && (
            <p style={{ fontSize: FS.s10_5, color: "var(--tm)", marginTop: 4 }}>
              {t("docgen.no_subfolders", {
                fallback: "No subfolders found in the case folder.",
              })}
            </p>
          )}
        </>
      )}
    </ModalLayout>
  );
}

export default GenerateDocButton;
